import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { useProgressSync } from '../app/providers/ProgressSyncProvider.jsx';
import { clearCloudProgress } from '../services/playback/cloudProgressService.js';
import {
  clearPlaybackProgress,
  contentPlaybackId,
  episodePlaybackKey,
  listPlaybackProgress,
  playbackProgressEventName,
  titlePlaybackKey,
  viewerProgressScope,
} from '../services/playback/playbackService.js';
import '../styles/playback.css';

function candidateMap(catalog) {
  const map = new Map();
  for (const item of catalog?.titles || []) {
    if (contentPlaybackId(item)) {
      const key = titlePlaybackKey(item);
      map.set(key, {
        mediaKey: key,
        title: item.title,
        subtitle: item.kind === 'series' ? 'Series' : 'Movie',
        image: item.backdrop || item.image || '',
        href: `/watch/${encodeURIComponent(item.id)}`,
      });
    }
    for (const episode of item.episodes || []) {
      if (!contentPlaybackId(episode)) continue;
      const key = episodePlaybackKey(item, episode);
      map.set(key, {
        mediaKey: key,
        title: episode.title,
        subtitle: `${item.title} • S${episode.season} E${episode.episode}`,
        image: episode.image || item.backdrop || item.image || '',
        href: `/watch/${encodeURIComponent(item.id)}/season/${episode.season}/episode/${episode.episode}`,
      });
    }
  }
  return map;
}

function formatResume(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  return `${minutes}m ${remainder}s`;
}

function ContinueWatchingCard({ item, viewerScope, user, onRemove }) {
  const ratio = item.duration > 0 ? Math.min(1, item.seconds / item.duration) : 0;
  return (
    <article className="continue-card" data-progress-key={item.mediaKey}>
      <NavLink className="continue-card-link" to={item.href} aria-label={`Resume ${item.title}`}>
        <div className="continue-card-art">
          {item.image ? <img src={item.image} alt={`${item.title} artwork`} loading="lazy" decoding="async" /> : <div className="artwork-fallback"><span>{item.title}</span></div>}
          <span className="continue-play" aria-hidden="true">▶</span>
          {item.duration > 0 ? (
            <span className="continue-progress-track" aria-hidden="true">
              <span className="continue-progress-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
            </span>
          ) : null}
        </div>
        <div className="continue-card-copy">
          <strong>{item.title}</strong>
          <span>{item.subtitle}</span>
          <small>Resume at {formatResume(item.seconds)}</small>
        </div>
      </NavLink>
      <button
        type="button"
        className="continue-remove"
        onClick={() => {
          clearPlaybackProgress(viewerScope, item.mediaKey);
          if (user?.id) clearCloudProgress(user, item.mediaKey).catch(() => {});
          onRemove(item.mediaKey);
        }}
        aria-label={`Remove ${item.title} from Continue Watching`}
      >
        Remove
      </button>
    </article>
  );
}

export function ContinueWatchingPage() {
  const { catalog, status, error, retry } = useCatalog();
  const { user } = useSession();
  const progressSync = useProgressSync();
  const viewerScope = useMemo(() => viewerProgressScope(user), [user?.id]);
  const candidates = useMemo(() => candidateMap(catalog), [catalog]);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const eventName = playbackProgressEventName();
    const refresh = (event) => {
      if (!event?.detail?.viewerScope || event.detail.viewerScope === viewerScope) setRevision((value) => value + 1);
    };
    window.addEventListener(eventName, refresh);
    return () => window.removeEventListener(eventName, refresh);
  }, [viewerScope]);

  const items = useMemo(() => listPlaybackProgress(viewerScope)
    .map((record) => {
      const candidate = candidates.get(record.mediaKey);
      return candidate ? { ...candidate, ...record } : null;
    })
    .filter(Boolean), [viewerScope, candidates, revision, progressSync.ready]);

  if (status === 'loading' || (user && !progressSync.ready)) return <section className="continue-page" aria-busy="true"><h1>Continue Watching</h1><p>Loading your progress…</p></section>;
  if (status === 'error') return <section className="continue-page"><h1>Continue Watching</h1><p>{error?.message || 'Unable to load the catalog.'}</p><button type="button" onClick={retry}>Try Again</button></section>;

  return (
    <section className="continue-page">
      <header className="collection-heading compact">
        <span className="eyebrow">WatchVIM</span>
        <h1>Continue Watching</h1>
        <p>{user ? 'Pick up where you left off across your signed-in WatchVIM devices.' : 'Pick up where you left off on this device.'}</p>
        {progressSync.error && user ? <small className="catalog-muted">Cloud sync is temporarily unavailable; local progress is still available.</small> : null}
      </header>
      {items.length ? (
        <div className="continue-grid">
          {items.map((item) => <ContinueWatchingCard key={item.mediaKey} item={item} viewerScope={viewerScope} user={user} onRemove={() => setRevision((value) => value + 1)} />)}
        </div>
      ) : (
        <div className="continue-empty">
          <h2>Nothing to resume yet.</h2>
          <p>Start a movie or episode and WatchVIM will keep your place here.</p>
          <NavLink className="hero-button primary" to="/home">Browse WatchVIM</NavLink>
        </div>
      )}
    </section>
  );
}
